import React, { useContext } from 'react';
import { Hidden, Box, Menu } from '@mui/material';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import { StudioClipActionProps } from './StudioClipAction';
import { StudioClipContext } from '../StudioClip';

/**
 * StudioClipActions component handles the rendering of all actions in either an inline
 * list (desktop) or a tucked away menu (mobile).
 *
 * @example
 * <StudioClipActions>
 *   <StudioClipActionDelete ... />
 *   <StudioClipActionDownload ... />
 *   ...
 * </StudioClipActions>
 */
const StudioClipActions: React.FC<{
  children: // Tricky, but false allows for inline conditional rendering
  | (React.ReactElement<StudioClipActionProps> | false)[]
    | React.ReactElement<StudioClipActionProps>;
}> = ({ children }) => {
  const { clip, setActionMenuAnchor, actionMenuAnchor } =
    useContext(StudioClipContext);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionMenuAnchor(event.currentTarget);
  };
  const handleMenuClose = () => {
    setActionMenuAnchor(null);
  };

  return (
    <>
      {/* Desktop options inline */}
      <Hidden smDown>
        <Box display="flex" alignItems="center" flexShrink={0}>
          {children}
        </Box>
      </Hidden>
      {/* Mobile options nested in dropdown menu */}
      <Hidden smUp>
        <Button
          variant="icon"
          aria-label="more"
          aria-controls={`studio-clip-options-menu-${clip.id}`}
          aria-haspopup="true"
          onClick={handleMenuOpen}
        >
          <Icon type="moreVert" />
        </Button>
        <Menu
          id={`studio-clip-options-menu-${clip.id}`}
          anchorEl={actionMenuAnchor}
          keepMounted
          open={Boolean(actionMenuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          style={{
            zIndex: 1200,
          }}
        >
          {/* Adding the onMouseUp handler to close the menu when an action is selected */}
          {React.Children.map(children, child => {
            if (React.isValidElement<StudioClipActionProps>(child)) {
              return React.cloneElement(child, {
                onMouseUp: handleMenuClose,
              } as Partial<StudioClipActionProps>);
            }
            return child;
          })}
        </Menu>
      </Hidden>
    </>
  );
};

export default StudioClipActions;
